/* eslint-disable no-console */
import React, {
    createContext,
    useContext,
    useReducer,
    useEffect,
    useCallback
} from "react";
import { firestore, feedAdsImageRef /*, functions*/ } from "../services/firebase";
import { generateId } from "./promotions";
import firebase from "firebase/app";
import { captureError, captureWarning } from "../lib/errorReporting";

const LOAD_SETTINGS = "LOAD_SETTINGS";
const LOAD_ADS = "LOAD_ADS";
const SET_LOADING = "SET_LOADING";
const SET_ERROR = "SET_ERROR";
const UPDATE_AD = "UPDATE_AD";
const REMOVE_AD = "REMOVE_AD";

const initialState = {
    settings: { adInterval: 10 },
    ads: [],
    isLoading: false,
    error: null
};

function feedAdsReducer(state, action) {
    switch (action.type) {
        case SET_LOADING:
            return { ...state, isLoading: action.payload, error: null };
        case SET_ERROR:
            return { ...state, isLoading: false, error: action.payload };
        case LOAD_SETTINGS:
            return { ...state, settings: action.payload, isLoading: false };
        case LOAD_ADS:
            return { ...state, ads: action.payload, isLoading: false };
        case UPDATE_AD: {
            const updatedAds = [...state.ads];
            const index = updatedAds.findIndex(ad => ad.id === action.payload.id);
            if (index > -1) {
                updatedAds[index] = action.payload;
            } else {
                updatedAds.push(action.payload);
            }
            return { ...state, ads: updatedAds, isLoading: false };
        }
        case REMOVE_AD:
            return {
                ...state,
                ads: state.ads.filter(ad => ad.id !== action.payload),
                isLoading: false
            };
        default:
            return state;
    }
}

const FeedAdsContext = createContext(initialState);
const FeedAdsUtilsContext = createContext({});

export function FeedAdsProvider({ children }) {
    const [state, dispatch] = useReducer(feedAdsReducer, initialState);

    const setLoading = (loading) => dispatch({ type: SET_LOADING, payload: loading });
    const setError = (error) => dispatch({ type: SET_ERROR, payload: error });

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const settingsRef = firestore.collection("appSettings").doc("feedAds");
            const doc = await settingsRef.get();
            if (doc.exists) {
                dispatch({ type: LOAD_SETTINGS, payload: doc.data() });
            } else {
                await settingsRef.set(initialState.settings);
                dispatch({ type: LOAD_SETTINGS, payload: initialState.settings });
            }
        } catch (err) {
            console.error("Error fetching feed ad settings:", err);
            captureError(err, { action: "fetchFeedAdSettings" });
            setError("Failed to load settings.");
        }
    }, []);

    const saveSetting = useCallback(async (settingKey, value) => {
        setLoading(true);
        try {
            const settingsRef = firestore.collection("appSettings").doc("feedAds");
            await settingsRef.update({ [settingKey]: value });
            dispatch({ type: LOAD_SETTINGS, payload: { ...state.settings, [settingKey]: value } });
            return true;
        } catch (err) {
            console.error(`Error saving setting ${settingKey}:`, err);
            captureError(err, { action: "saveFeedAdSetting", settingKey });
            setError(`Failed to save setting ${settingKey}.`);
            return false;
        }
    }, [state.settings]);

    const fetchAds = useCallback(async () => {
        setLoading(true);
        try {
            const snapshot = await firestore.collection("FeedAds").orderBy("createdAt", "desc").get();
            const adsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            dispatch({ type: LOAD_ADS, payload: adsData });
        } catch (err) {
            console.error("Error fetching feed ads:", err);
            captureError(err, { action: "fetchFeedAds" });
            setError("Failed to load feed ads.");
        }
    }, []);

    const uploadAdImage = useCallback(async (file) => {
        setLoading(true);
        const fileName = `${generateId()}_${file.name}`;
        const fileRef = feedAdsImageRef.child(fileName);
        try {
            const snapshot = await fileRef.put(file);
            const downloadUrl = await snapshot.ref.getDownloadURL();
            setLoading(false);
            return { fileName, downloadUrl };
        } catch (err) {
            console.error("Error uploading ad image:", err);
            captureError(err, { action: "uploadFeedAdImage" });
            setError("Failed to upload image.");
            setLoading(false);
            throw err;
        }
    }, []);

    const saveAd = useCallback(async (adData) => {
        setLoading(true);
        const collectionRef = firestore.collection("FeedAds");
        const now = firebase.firestore.Timestamp.now();

        const dataToSave = {
            ...adData,
            startDate: adData.startDate ? firebase.firestore.Timestamp.fromDate(adData.startDate.toDate()) : null,
            endDate: adData.endDate ? firebase.firestore.Timestamp.fromDate(adData.endDate.toDate()) : null,
            updatedAt: now,
            metrics: adData.metrics || { impressions: 0, clicks: 0 },
        };

        try {
            let docRef;
            if (adData.id) {
                docRef = collectionRef.doc(adData.id);
                await docRef.update(dataToSave);
            } else { 
                dataToSave.createdAt = now;
                delete dataToSave.id;
                docRef = await collectionRef.add(dataToSave);
                dataToSave.id = docRef.id;
            }
            dispatch({ type: UPDATE_AD, payload: { ...dataToSave } });
            return { ...dataToSave };
        } catch (err) {
            console.error("Error saving feed ad:", err);
            captureError(err, { action: "saveFeedAd" });
            setError("Failed to save feed ad.");
            return null;
        }
    }, []);

    const deleteAd = useCallback(async (adId, fileName) => {
        setLoading(true);
        try {
            await firestore.collection("FeedAds").doc(adId).delete();

            if (fileName) {
                try {
                    await feedAdsImageRef.child(fileName).delete();
                } catch (storageError) {
                    console.warn(`Failed to delete ad image ${fileName}:`, storageError);
                    captureWarning(`Failed to delete ad image ${fileName}`, { error: storageError?.message });
                }
            }

            dispatch({ type: REMOVE_AD, payload: adId });
            return true;
        } catch (err) {
            console.error("Error deleting feed ad:", err);
            captureError(err, { action: "deleteFeedAd" });
            setError("Failed to delete feed ad.");
            return false;
        }
    }, []);


    useEffect(() => {
        fetchSettings();
        fetchAds();
    }, [fetchSettings, fetchAds]);

    const utils = {
        fetchSettings,
        saveSetting,
        fetchAds,
        saveAd,
        deleteAd,
        uploadAdImage
    };

    return (
        <FeedAdsContext.Provider value={state}>
            <FeedAdsUtilsContext.Provider value={utils}>
                {children}
            </FeedAdsUtilsContext.Provider>
        </FeedAdsContext.Provider>
    );
}

export const useFeedAds = () => useContext(FeedAdsContext);
export const useFeedAdsUtils = () => useContext(FeedAdsUtilsContext);